import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import {
    getFleetSummaryCalendarApi,
    getFleetSummaryGetIdApi,
    postFleetSummaryApi,
    getFleetSummaryByIdApi,
    getFleetSummaryGetDayIdApi,
    getFleetSummaryGetWeekIdApi,
    postFleetSummaryWeekApi,
    getActivityDiaryDictTreeApi
} from 'api'
import dayjs from 'dayjs'
import { BoundarySlice } from 'redux/reducers'

const initialState = {
    calendarInfo: null, // 被点击的日历信息
    calendarData: null, // 日历上当前范围内显示的总结填写信息
    dictData: null, // 数据字典
    listData: null, // 回显数据
    locationName: '', // location 名称
    locationId: '', // location id
    yearDate: dayjs().format('YYYY'), // 年份
    save: false,
    id: '',
}

/* 获取日历上的数据 */
export const getFleetSummaryCalendarAction = createAsyncThunk(
    'FleetSummarySlice/getFleetSummaryCalendarAction',
    async (params, thunkAPI) => {
        try {
            const { data } = await getFleetSummaryCalendarApi(params)
            return data.data
        } catch (error) {
            thunkAPI.dispatch(BoundarySlice.actions.addError(error))
        }
    }
)


/* 通过日期获取 日 id */
export const getFleetSummaryByDateDayAction = createAsyncThunk(
    'FleetSummarySlice/getFleetSummaryByDateDayAction',
    async (listDataParams, thunkAPI) => {
        try {
            const commentData = await getFleetSummaryGetDayIdApi(listDataParams) // 通过日期查询 id
            return commentData?.data?.data?.id
        } catch (error) {
            thunkAPI.dispatch(BoundarySlice.actions.addError(error))
        }
    }
)

/* 通过日期获取 月 id */
export const getFleetSummaryByDateAction = createAsyncThunk(
    'FleetSummarySlice/getFleetSummaryByDateAction',
    async (listDataParams, thunkAPI) => {
        try {
            const commentData = await getFleetSummaryGetIdApi(listDataParams) // 通过日期查询 id
            return commentData?.data?.data?.id
        } catch (error) {
            thunkAPI.dispatch(BoundarySlice.actions.addError(error))
        }
    }
)

/* 通过日期获取 周 id */
export const getFleetSummaryByDateWeekAction = createAsyncThunk(
    'FleetSummarySlice/getFleetSummaryByDateWeekAction',
    async (listDataParams, thunkAPI) => {
        try {
            const commentData = await getFleetSummaryGetWeekIdApi(listDataParams) // 通过日期查询 id
            return commentData?.data?.data?.id
        } catch (error) {
            thunkAPI.dispatch(BoundarySlice.actions.addError(error))
        }
    }
)

/* 获取数据 */
export const getFleetSummaryDataAction = createAsyncThunk(
    'FleetSummarySlice/getFleetSummaryDataAction',
    async (id, thunkAPI) => {
        const commentData = await getFleetSummaryByIdApi(id) // 通过 id 查询
        return Promise.all([commentData])
            .then(listData => listData)
            .catch(error => thunkAPI.dispatch(BoundarySlice.actions.addError(error)))
    }
)

/* 保存数据 */
export const postFleetSummaryDataAction = createAsyncThunk(
    'FleetSummarySlice/postFleetSummaryDataAction',
    async ({ id, commentParams, isCopyData }, { dispatch }) => {
        let vid
        try {
            if (!id || isCopyData) {
                const commentData = await postFleetSummaryApi(commentParams) // 新增
                vid = commentData?.data?.data?.id
                dispatch(FleetSummarySlice.actions.addId(vid))
                return { isTrue: true, id: vid } // 已保存状态
            } else {
                vid = id
                await postFleetSummaryApi({...commentParams, id}) // 修改
                return { isTrue: true, id } // 已保存状态
            }
        } catch (error) {
            dispatch(FleetSummarySlice.actions.addId(vid)) // 失败时，保存 id
            dispatch(BoundarySlice.actions.addError(error))
        }
    }
)

/* 获取数据字典 */
export const getFleetSummaryDictDataAction = createAsyncThunk(
    'FleetSummarySlice/getFleetSummaryDictDataAction',
    async (dictType, thunkAPI) => {
        const activityTypeDataDict = getActivityDiaryDictTreeApi(dictType[0])
        const problemTypeDataDict = getActivityDiaryDictTreeApi(dictType[1])
        return Promise.all([activityTypeDataDict, problemTypeDataDict])
            .then(dictData => dictData)
            .catch(error => thunkAPI.dispatch(BoundarySlice.actions.addError(error)))
    }
)

/* 保存数据  周/月 */
export const postFleetSummaryWeekDataAction = createAsyncThunk(
    'FleetSummarySlice/postFleetSummaryWeekDataAction',
    async ({ id, commentParams, isCopyData }, { dispatch }) => {
        let vid
        try {
            if (!id || isCopyData) {
                const commentData = await postFleetSummaryWeekApi(commentParams) // 新增
                vid = commentData?.data?.data?.id
                dispatch(FleetSummarySlice.actions.addId(vid))
                return { isTrue: true, id: vid } // 已保存状态
            } else {
                vid = id
                await postFleetSummaryWeekApi({ ...commentParams, id }) // 修改
                return { isTrue: true, id } // 已保存状态
            }
        } catch (error) {
            dispatch(FleetSummarySlice.actions.addId(vid)) // 失败时，保存 id
            dispatch(BoundarySlice.actions.addError(error))
        }
    }
)

export const FleetSummarySlice = createSlice({
    name: 'FleetSummarySlice',
    initialState,
    reducers: {
        /* 日历点击信息 */
        addCalendarInfo: (state, action) => {
            state.calendarInfo = action.payload
        },
        /* 清空日历与详情页信息 */
        clearListData: state => {
            state.listData = null
            state.calendarData = null
            state.save = false
            state.id = ''
        },
        /* 当前日历主 id */
        addId: (state, action) => {
            state.id = action.payload
        },
        /* location 名称 */
        addLocationName: (state, action) => {
            state.locationName = action.payload
        },
        /* location id */
        addLocationId: (state, action) => {
            state.locationId = action.payload
        },
        addYearDate: (state, action) => {
            state.yearDate = action.payload
        },
    },
    extraReducers: {
        /* 获取日历上的数据 */
        [getFleetSummaryCalendarAction.fulfilled.type]: (state, action) => {
            state.calendarData = action.payload
        },

        /* 提交总结数据 */
        [postFleetSummaryDataAction.fulfilled.type]: (state, action) => {
            state.save = action.payload
        },

        /* 处理数据字典 */
        [getFleetSummaryDictDataAction.fulfilled.type]: (state, action) => {
            state.dictData = action.payload
        },

        /* 提交总结数据 周/月 */
        [postFleetSummaryWeekDataAction.fulfilled.type]: (state, action) => {
            state.save = action.payload
        },

        /* 获取回显数据 */
        [getFleetSummaryDataAction.fulfilled.type]: (state, action) => {
            state.listData = action.payload
        },

        /* 通过日期，获取 日 id */
        [getFleetSummaryByDateDayAction.fulfilled.type]: (state, action) => {
            state.id = action.payload
        },

        /* 通过日期，获取 月 id */
        [getFleetSummaryByDateAction.fulfilled.type]: (state, action) => {
            state.id = action.payload
        },

        /* 通过日期，获取 周 id */
        [getFleetSummaryByDateWeekAction.fulfilled.type]: (state, action) => {
            state.id = action.payload
        },
    },
})
